import { ArrowRightSvg } from '@/assets/svg';
import { ResumeDigitalTwin } from '@/domain/DigitalTwin/DigitalTwin';
import Icon, { EyeOutlined, EyeInvisibleOutlined, SettingOutlined, CloseOutlined } from '@ant-design/icons';
import { Button, Checkbox, Select, Spin, Tooltip } from 'antd';
import StatusBadge, { StatusType } from '../DigitalTwinsPopover/StatusBadge';
import ParameterCounter from '../DigitalTwinsPopover/ParameterCounter';
import { useMemo } from 'react';
import { useDigitalTwinsContext, ParameterClass } from '@/contexts/DigitalTwinsContext';

interface DigitalTwinsContentProps {
	loading: boolean;
	digitalTwinsData: ResumeDigitalTwin[] | null;
	selectedDigitalTwinId: string | null;
	onSelectDigitalTwin?: (id: string) => void;
}

function DigitalTwinsContent({ 
	loading, 
	digitalTwinsData,
	selectedDigitalTwinId,
	onSelectDigitalTwin 
}: DigitalTwinsContentProps) {
	const { setHoveredParameterClass, lockedParameterClass, setLockedParameterClass, hiddenParameterClasses, setHiddenParameterClasses, showGisLayer, setShowGisLayer } = useDigitalTwinsContext();

	// Find selected digital twin data
	const selectedDT = useMemo(() => {
		if (!selectedDigitalTwinId || !digitalTwinsData) return null;
		return digitalTwinsData.find(dt => dt.inpId.toString() === selectedDigitalTwinId);
	}, [selectedDigitalTwinId, digitalTwinsData]);

	// Handlers for hover
	const handleMouseEnter = (paramClass: ParameterClass) => {
		// Only apply hover if nothing is locked
		if (!lockedParameterClass) {
			setHoveredParameterClass(paramClass);
		}
	};

	const handleMouseLeave = () => {
		// Only clear hover if nothing is locked
		if (!lockedParameterClass) {
			setHoveredParameterClass(null);
		}
	};

	const handleCounterClick = (paramClass: ParameterClass) => {
		// Toggle: if clicking the same class, unlock it; otherwise, lock the new one
		if (lockedParameterClass === paramClass) {
			setLockedParameterClass(null);
			setHoveredParameterClass(null);
		} else {
			setLockedParameterClass(paramClass);
			setHoveredParameterClass(paramClass);
		}
	};

	const handleHideShowToggle = (paramClass: ParameterClass) => {
		setHiddenParameterClasses(prev => {
			const newSet = new Set(prev);
			if (newSet.has(paramClass)) {
				newSet.delete(paramClass);
			} else {
				newSet.add(paramClass);
			}
			return newSet;
		});
	};

	// Parse smart controls from CSV string
	const smartControlsList = useMemo(() => {
		if (!selectedDT?.smart_controls) return [];
		return selectedDT.smart_controls.split(',').map(control => control.trim());
	}, [selectedDT?.smart_controls]);

	// Format date for display (from "2025-11-18 02:30:07 AM" to "2025/11/18 02:30 AM")
	const formatDate = (dateStr: string) => {
		if (!dateStr) return '';
		// Remove seconds from time
		return dateStr.replace(/(\d{4})-(\d{2})-(\d{2}) (\d{2}:\d{2}):\d{2}/, '$1/$2/$3 $4');
	};

	return (
		<div className="h-full flex flex-col">
			{loading ? (
				<div className="flex justify-center items-center h-96">
					<Spin size="large" />
				</div>
			) : !digitalTwinsData || digitalTwinsData.length === 0 ? (
				<div className="flex flex-col justify-center items-center h-96 text-gray-500">
					<p className="text-lg font-medium">No Digital Twins Available</p>
					<p className="text-sm mt-2">Please select a location with Digital Twin data</p>
				</div>
			) : (
				<div className="flex flex-col px-5 py-2 pb-5">
					<div className="flex items-center py-3 border-b border-gray-200 gap-2">
						<Checkbox
							checked={showGisLayer}
							onChange={(e) => setShowGisLayer(e.target.checked)}
						/>
						<span className="text-sm text-gray-700">Show GIS map layer</span>
						<span className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: '#1e96be' }} />
					</div>
					<div className="flex justify-between items-center py-4 border-b border-gray-200 gap-4">
						<div className="w-1/2 text-gray-500 text-sm">Digital Twin</div>
						<div className="flex-1 flex items-center gap-2 flex-wrap">
							<Select
								value={selectedDigitalTwinId}
								placeholder="Select Digital Twin"
								className="flex-1 min-w-[150px]"
								onChange={onSelectDigitalTwin}
								loading={loading}
								options={digitalTwinsData?.map((dt) => ({
									value: dt.inpId.toString(),
									label: dt.inpId.toString(),
								}))}
							/>
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div className="flex justify-between items-start py-4 border-b border-gray-200 gap-4">
						<div className="w-1/2 text-gray-500 items-center text-sm">Hydraulic Model</div>
						<div className="flex-1 flex items-center gap-2 flex-wrap">
							<span className="flex-1 text-gray-800">
								{selectedDT ? `${selectedDT.inpName}` : '-'}
							</span>
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div 
						className={`flex justify-between items-start py-4 border-b border-gray-200 gap-4 cursor-pointer transition-all duration-200 ${
							lockedParameterClass === 'hydrotwins' 
								? 'bg-blue-50' 
								: 'hover:bg-blue-50 '
						}`}
						onMouseEnter={() => handleMouseEnter('hydrotwins')}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex-[0_0_40%] text-gray-500 items-center text-sm"># HydroTwins</div>
						<div className="flex-1 flex items-center gap-2">
							<ParameterCounter 
								value={selectedDT?.hydrotwins_count || 0} 
								onClick={() => handleCounterClick('hydrotwins')}
							/>
							{lockedParameterClass === 'hydrotwins' ? (
								<Button type="link" size="small" onClick={() => handleCounterClick('hydrotwins')}>
									<CloseOutlined /> Exit
								</Button>
							) : (
								<Button type="link" size="small" onClick={() => handleHideShowToggle('hydrotwins')}>
									{hiddenParameterClasses.has('hydrotwins') ? (
										<><Tooltip title="Set as Visible"><EyeInvisibleOutlined /></Tooltip></>
									) : (
										<><Tooltip title="Set as Invisible"><EyeOutlined /></Tooltip></>
									)}
								</Button>
							)}
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div 
						className={`flex justify-between items-start py-4 border-b border-gray-200 gap-4 cursor-pointer transition-all duration-200 ${
							lockedParameterClass === 'historical' 
								? 'bg-blue-50' 
								: 'hover:bg-blue-50'
						}`}
						onMouseEnter={() => handleMouseEnter('historical')}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex-[0_0_40%] text-gray-500 items-center text-sm"># Historical Parameters</div>
						<div className="flex-1 flex items-center gap-2">
							<ParameterCounter 
								value={selectedDT?.historical_count || 0} 
								onClick={() => handleCounterClick('historical')}
							/>
							{lockedParameterClass === 'historical' ? (
								<Button type="link" size="small" onClick={() => handleCounterClick('historical')}>
									<CloseOutlined /> Exit
								</Button>
							) : (
								<Button type="link" size="small" onClick={() => handleHideShowToggle('historical')}>
									{hiddenParameterClasses.has('historical') ? (
										<><Tooltip title="Set as Visible"><EyeInvisibleOutlined /></Tooltip></>
									) : (
										<><Tooltip title="Set as Invisible"><EyeOutlined /></Tooltip></>
										
									)}
								</Button>
							)}
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div 
						className={`flex justify-between items-start py-4 border-b border-gray-200 gap-4 cursor-pointer transition-all duration-200 ${
							lockedParameterClass === 'forecast' 
								? 'bg-blue-50' 
								: 'hover:bg-blue-50'
						}`}
						onMouseEnter={() => handleMouseEnter('forecast')}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex-[0_0_40%] text-gray-500 items-center text-sm"># Forecasted Parameters</div>
						<div className="flex-1 flex items-center gap-2">
							<ParameterCounter 
								value={selectedDT?.forecast_count || 0} 
								onClick={() => handleCounterClick('forecast')}
							/>
							{lockedParameterClass === 'forecast' ? (
								<Button type="link" size="small" onClick={() => handleCounterClick('forecast')}>
									<CloseOutlined /> Exit
								</Button>
							) : (
								<Button type="link" size="small" onClick={() => handleHideShowToggle('forecast')}>
									{hiddenParameterClasses.has('forecast') ? (
										<><Tooltip title="Set as Visible"><EyeInvisibleOutlined /></Tooltip></>
									) : (
										<><Tooltip title="Set as Invisible"><EyeOutlined /></Tooltip></>
										
									)}
								</Button>
							)}
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div 
						className={`flex justify-between items-center py-4 border-b border-gray-200 gap-4 cursor-pointer transition-all duration-200 ${
							lockedParameterClass === 'calculated' 
								? 'bg-blue-50' 
								: 'hover:bg-blue-50'
						}`}
						onMouseEnter={() => handleMouseEnter('calculated')}
						onMouseLeave={handleMouseLeave}
					>
						<div className="flex-[0_0_40%] text-gray-500 items-center text-sm"># Calculated Parameters</div>
						<div className="flex-1 flex items-center gap-2">
							<ParameterCounter 
								value={selectedDT?.calculated_count || 0} 
								onClick={() => handleCounterClick('calculated')}
							/>
							{lockedParameterClass === 'calculated' ? (
								<Button type="link" size="small" onClick={() => handleCounterClick('calculated')}>
									<CloseOutlined /> Exit
								</Button>
							) : (
								<Button type="link" size="small" onClick={() => handleHideShowToggle('calculated')}>
									{hiddenParameterClasses.has('calculated') ? (
										<><Tooltip title="Set as Visible"><EyeInvisibleOutlined /></Tooltip></>
									) : (
										<><Tooltip title="Set as Invisible"><EyeOutlined /></Tooltip></>
									)}
								</Button>
							)}
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] flex-shrink-0"
							/> */}
						</div>
					</div>
					<div className="flex justify-between items-start py-4 border-b border-gray-200 gap-4">
						<div className="flex-[0_0_40%] text-gray-500 text-sm">Smart Controls</div>
						<div className="flex-1 flex flex-col justify-between gap-2">
							{smartControlsList.length > 0 ? (
								<div className="flex flex-wrap gap-2 flex-col ">
									{smartControlsList.map((control, index) => (
										<span 
											key={index}
											className="px-2 py-1 text-xs font-medium"
										>
											{control}
										</span>
									))}
								</div>
							) : (
								<span className="text-gray-800">-</span>
							)}
							{/* {smartControlsList.length > 0 && (
								<Button type="link" size="small" onClick={() => {}} className="self-start">
									<Icon component={ArrowRightSvg} className="text-[#078CB3]" />
									View
								</Button>
							)} */}
						</div>
					</div>
					<div className="flex justify-between items-start py-4 gap-4">
						<div className="flex-[0_0_40%] text-gray-500 text-sm pt-1">Latest Simulations</div>
						<div className="flex-1 flex flex-col items-start gap-3">
							<div className="flex flex-col gap-2 w-full">
								{selectedDT?.latest_historical_sim && (
									<StatusBadge 
										status={selectedDT.latest_historical_sim.status.toUpperCase() as StatusType} 
										prefixText={`Historical: `} 
										date={formatDate(selectedDT.latest_historical_sim.date)} 
									/>
								)}
								{selectedDT?.latest_forecast_sim && (
									<StatusBadge 
										status={selectedDT.latest_forecast_sim.status.toUpperCase() as StatusType} 
										prefixText={`Forecast: `} 
										date={formatDate(selectedDT.latest_forecast_sim.date)} 
									/>
								)}
							</div>
							{/* <Button
								type="text"
								icon={<SettingOutlined />}
								className="text-[#078CB3] hover:text-[#056a8a] hover:bg-[#f0f9fc] self-end"
							/> */}
						</div>
					</div>
				</div>
			)}
		</div>
	);
}

export default DigitalTwinsContent;
